import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { DocumentService, InsightsService, sharedApiClient } from '../fetcher';
import {
  IdentityService,
  TeamService,
  UserAbility,
  useNotifications,
  useBugReportContext,
  BROWSER_PAGE_LABEL,
  medPromptBrowserTitle,
  getAssigneeName as resolveAssigneeName,
  isAbortError,
} from '@medprompt/shared';
import { StatusEnum } from '../enums/statusEnum';
import { getNextRiskCategory } from '../enums/riskCategory';
import { DOCUMENT_MISSING_REASON } from './MissingDocumentView';

function isDocumentNotFoundError(error) {
  return error?.status === 400 || error?.status === 404;
}

const useDocumentRecord = ({ documentId, user }) => {
  const { addNotification } = useNotifications();
  const { setBugContext, clearBugContext } = useBugReportContext();
  const documentService = useMemo(() => new DocumentService(sharedApiClient), []);
  const insightsService = useMemo(() => new InsightsService(sharedApiClient), []);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missingReason, setMissingReason] = useState(null);
  const [showMissingAuditModal, setShowMissingAuditModal] = useState(false);
  const [docFetchNonce, setDocFetchNonce] = useState(0);
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Monotonic token to discard stale doc-scoped fetches (patient, duplicates,
  // comms, workflow logs). Incremented whenever the target document changes so
  // in-flight responses for a previous document can never overwrite current state.
  const docScopedFetchIdRef = useRef(0);
  // The token above discards stale responses, but the server has already done the work by then.
  // This cancels them on the same axis, so navigating away frees the request thread immediately.
  const docScopedAbortRef = useRef(null);

  /** Invalidate and cancel every doc-scoped fetch, then open a fresh scope. */
  const beginDocScope = useCallback(() => {
    docScopedFetchIdRef.current++;
    docScopedAbortRef.current?.abort();
    docScopedAbortRef.current = new AbortController();
    return docScopedFetchIdRef.current;
  }, []);

  const getDocScopeId = useCallback(() => docScopedFetchIdRef.current, []);

  /** Options for a fetch belonging to the current doc scope. */
  const docScopeOptions = useCallback(() => {
    if (!docScopedAbortRef.current) {
      docScopedAbortRef.current = new AbortController();
    }
    return { signal: docScopedAbortRef.current.signal };
  }, []);

  const showDeletedPlaceholder = useCallback(() => {
    setDoc(null);
    setMissingReason(DOCUMENT_MISSING_REASON.DELETED);
    setLoading(false);
  }, []);

  // Accepts either the next document or an updater. The updater form is what patches applied
  // after an await must use: spreading a `doc` captured before the request would revert whatever
  // arrived from the update stream while that request was in flight.
  const updateDoc = useCallback((update) => {
    setDoc(prevDoc => (typeof update === 'function' ? update(prevDoc) : update));
  }, []);

  useEffect(() => {
    document.title = medPromptBrowserTitle(BROWSER_PAGE_LABEL.documentDetail);
  }, []);

  useEffect(() => {
    if (documentId) {
      setBugContext(`Document ID: ${documentId}\n\n`);
    }
    return () => clearBugContext();
  }, [documentId, setBugContext, clearBugContext]);

  // Leaving the view should release the server's threads too, not just this component's state.
  useEffect(() => () => docScopedAbortRef.current?.abort(), []);

  // Fetch document data when documentId changes
  useEffect(() => {
    if (!documentId) return;

    // Track if this effect has been cancelled (component unmounted or documentId changed)
    let isCancelled = false;
    // This effect owns the /data request. Sibling effects must not abort it.
    const documentFetchController = new AbortController();
    let retryTimeoutId = null;
    let timeoutId = null;

    setLoading(true);
    setDoc(null); // Clear previous document
    setMissingReason(null);
    setShowMissingAuditModal(false);
    setDeleteModalOpen(false);
    // Invalidate secondary doc-scoped fetches (patient, comms, duplicates, workflow).
    // Do not use this signal for /data — those effects have a different lifetime.
    beginDocScope();
    console.log('Fetching document:', documentId);

    // Validate documentId format
    if (typeof documentId !== 'string' || documentId.trim() === '') {
      console.error('Invalid documentId provided:', documentId);
      setLoading(false);
      setDoc(null);
      setMissingReason(DOCUMENT_MISSING_REASON.NEVER_EXISTED);
      return;
    }

    const finishMissing = (reason) => {
      if (isCancelled) return;
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
      setDoc(null);
      setMissingReason(reason);
      setLoading(false);
    };

    timeoutId = setTimeout(() => {
      if (isCancelled) return;
      console.error('Document fetch timed out after 30 seconds for documentId:', documentId);
      documentFetchController.abort();
      finishMissing(DOCUMENT_MISSING_REASON.ERROR);
    }, 30000);

    const fetchWithRetry = async (retryCount = 0) => {
      if (isCancelled) {
        console.log('Fetch cancelled before attempt:', retryCount);
        return;
      }

      try {
        const fetchedDoc = await documentService.fetchDocumentData(documentId, {
          signal: documentFetchController.signal,
        });

        if (isCancelled) {
          console.log('Fetch completed but effect was cancelled, ignoring result');
          return;
        }

        clearTimeout(timeoutId);
        console.log('Document fetched successfully:', fetchedDoc?.documentId);
        if (fetchedDoc && fetchedDoc.documentId === documentId) {
          setDoc(fetchedDoc);
          setMissingReason(null);
          setLoading(false);
          return;
        }

        console.error('Fetched document mismatch or null. Expected:', documentId, 'Got:', fetchedDoc?.documentId);
        if (retryCount < 2) {
          console.log('Retrying document fetch, attempt:', retryCount + 1);
          retryTimeoutId = setTimeout(() => fetchWithRetry(retryCount + 1), 1000);
          return;
        }
        finishMissing(DOCUMENT_MISSING_REASON.ERROR);
      } catch (error) {
        if (isCancelled) {
          console.log('Fetch errored but effect was cancelled, ignoring');
          return;
        }

        if (isAbortError(error)) {
          finishMissing(DOCUMENT_MISSING_REASON.ERROR);
          return;
        }

        console.error('Error fetching document:', error);

        if (isDocumentNotFoundError(error)) {
          try {
            await insightsService.fetchDocumentAudit(documentId, {
              signal: documentFetchController.signal,
            });
            if (isCancelled) return;
            finishMissing(DOCUMENT_MISSING_REASON.DELETED);
          } catch (auditError) {
            if (isCancelled) return;
            if (isAbortError(auditError)) {
              finishMissing(DOCUMENT_MISSING_REASON.ERROR);
              return;
            }
            if (isDocumentNotFoundError(auditError)) {
              finishMissing(DOCUMENT_MISSING_REASON.NEVER_EXISTED);
              return;
            }
            finishMissing(DOCUMENT_MISSING_REASON.ERROR);
          }
          return;
        }

        if (retryCount < 2) {
          console.log('Retrying document fetch after error, attempt:', retryCount + 1);
          retryTimeoutId = setTimeout(() => fetchWithRetry(retryCount + 1), 1000);
          return;
        }

        finishMissing(DOCUMENT_MISSING_REASON.ERROR);
      }
    };

    fetchWithRetry();

    return () => {
      console.log('Document fetch effect cleanup, cancelling for documentId:', documentId);
      isCancelled = true;
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutId);
      documentFetchController.abort();
    };
  }, [documentId, documentService, insightsService, beginDocScope, docFetchNonce]);

  useEffect(() => {
    const identityManager = new IdentityService();
    const teamManager = new TeamService();

    identityManager.fetchActiveClinicUsers()
      .then((users) => {
        setActiveUsers(users || []);
      })
      .catch((error) => {
        console.error('Failed to fetch active users:', error);
        setActiveUsers([]);
      });

    teamManager.fetchTeams()
      .then((teamsData) => {
        setTeams(teamsData || []);
      })
      .catch((error) => {
        console.error('Failed to fetch teams:', error);
        setTeams([]);
      });
  }, []);

  /** Refetch after a reconnect. Resolves true only when the refreshed document was applied. */
  const refetchDocument = useCallback(async () => {
    try {
      const refreshedDoc = await documentService.fetchDocumentData(documentId, docScopeOptions());
      setDoc(prevDoc => (
        prevDoc && prevDoc.documentId === documentId ? refreshedDoc : prevDoc
      ));
      return true;
    } catch (error) {
      if (!isAbortError(error)) {
        console.error(`DocumentDetailView: Error refetching ${documentId} after reconnect:`, error);
      }
      return false;
    }
  }, [documentId, documentService, docScopeOptions]);

  const hasDocsWrite = Boolean(user?.authorities?.includes(UserAbility.DOCS_WRITE.key));
  const docStatus = doc?.status;

  // Check if regeneration buttons should be enabled (document is in READY state and not filed away)
  const isRegenerationEnabled = useCallback(() => (
    docStatus === StatusEnum.READY.value && hasDocsWrite
  ), [docStatus, hasDocsWrite]);

  // Check if editing is enabled (has write permissions and document is READY)
  const isEditingEnabled = useCallback(() => (
    hasDocsWrite && docStatus === StatusEnum.READY.value
  ), [docStatus, hasDocsWrite]);

  // Check if document can be filed away (has both nhsId and patientId)
  const canFileAway = Boolean(doc?.nhsId && doc?.patientId);
  const canEditRiskCategory = hasDocsWrite && docStatus === StatusEnum.READY.value;

  // Called after a regeneration is triggered; nothing to update locally yet.
  const handleRegenerationClick = useCallback(() => {
    console.log("Content regeneration triggered");
  }, []);

  const getAssigneeName = useCallback((assigneeId) => (
    resolveAssigneeName(assigneeId, activeUsers, teams)
  ), [activeUsers, teams]);

  const handleRiskCategoryCycle = useCallback(async () => {
    if (!canEditRiskCategory || !doc?.riskCategory) return;

    const targetDocumentId = doc.documentId;
    const previous = doc.riskCategory;
    const next = getNextRiskCategory(previous);
    updateDoc(prevDoc => (prevDoc?.documentId === targetDocumentId
      ? { ...prevDoc, riskCategory: next }
      : prevDoc));

    try {
      await documentService.updateDocument(targetDocumentId, { riskCategory: next });
    } catch (error) {
      console.error('Error updating risk category:', error);
      updateDoc(prevDoc => (prevDoc?.documentId === targetDocumentId
        ? { ...prevDoc, riskCategory: previous }
        : prevDoc));
      addNotification(`risk-category-error-${targetDocumentId}`, 'Failed to update urgency', { isError: true });
    }
  }, [canEditRiskCategory, doc?.documentId, doc?.riskCategory, documentService, updateDoc, addNotification]);

  const handleAddComment = useCallback(async (commentText) => {
    if (!commentText.trim() || !doc) return;
    const targetDocumentId = doc.documentId;

    try {
      await documentService.addDocumentComment(targetDocumentId, commentText);
      console.log('Comment added successfully');

      // Refresh document data to get updated comments
      const updatedDoc = await documentService.fetchDocumentData(targetDocumentId);
      setDoc(prevDoc => (prevDoc?.documentId === targetDocumentId ? updatedDoc : prevDoc));

      addNotification(`comment-${targetDocumentId}`, 'Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      addNotification(`comment-error-${targetDocumentId}`, 'Failed to add comment', { isError: true });
      throw error; // Re-throw so CommentsPanel can handle loading state
    }
  }, [doc, documentService, addNotification]);

  const openDeleteModal = useCallback(() => setDeleteModalOpen(true), []);
  const closeDeleteModal = useCallback(() => setDeleteModalOpen(false), []);

  // Confirm delete and stay on the deleted-document placeholder
  const confirmDelete = useCallback(() => {
    if (!hasDocsWrite || !doc) return;
    const targetDocumentId = doc.documentId;
    // Close modal immediately to provide instant feedback
    setDeleteModalOpen(false);

    documentService.deleteDocument(targetDocumentId)
      .then(() => {
        showDeletedPlaceholder();
      })
      .catch(error => {
        console.error('Error deleting document:', error);
        addNotification(`delete-error-${targetDocumentId}`, 'Failed to delete document', { isError: true });
      });
  }, [hasDocsWrite, doc, documentService, showDeletedPlaceholder, addNotification]);

  const retryLoad = useCallback(() => setDocFetchNonce((nonce) => nonce + 1), []);
  const openMissingAuditTrail = useCallback(() => setShowMissingAuditModal(true), []);
  const closeMissingAuditTrail = useCallback(() => setShowMissingAuditModal(false), []);

  return {
    documentService,
    insightsService,
    doc,
    loading,
    missingReason,
    showMissingAuditModal,
    openMissingAuditTrail,
    closeMissingAuditTrail,
    retryLoad,
    // Changes whenever a fresh load of the document starts, including a retry.
    loadKey: `${documentId}:${docFetchNonce}`,
    activeUsers,
    teams,
    getAssigneeName,
    getDocScopeId,
    docScopeOptions,
    updateDoc,
    refetchDocument,
    showDeletedPlaceholder,
    isRegenerationEnabled,
    isEditingEnabled,
    canFileAway,
    canEditRiskCategory,
    handleRegenerationClick,
    handleRiskCategoryCycle,
    handleAddComment,
    deleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  };
};

export default useDocumentRecord;
